import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import {
  createStyles,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import "jspdf-autotable";
import { CustomTheme } from "../../../../types/customTheme";
import WithLoading from "../../../../utils/WithLoading";
import RGL, { WidthProvider } from "react-grid-layout";
import SingleWidgetBox from "./SingleWidgetBox";
import { GlobalContext } from "../../../../root/App";
import { setLocalStore } from "../../../../utils/store/localStore";
import TextWidgetBox from "./TextWidgetBox";
import EmptyWidgetBoxes from "./EmptyWidgetBoxes";
const ReactGridLayout = WidthProvider(RGL);
const GRID_BASE_WIDTH = 1200; // reference width at which widget fontSizes render at 100%
const MIN_FONT_SCALE = 0.5;
const USER_FONT_SCALE_STEP = 0.1;
const MIN_USER_FONT_SCALE = 0.5;
const MAX_USER_FONT_SCALE = 2;
const GRID_MARGIN_Y = 10; // react-grid-layout default vertical margin
const GRID_BOTTOM_SPACING = 12; // breathing space under the last row
const MIN_ROW_HEIGHT = 60;
export interface DashboardCreateModalProps {
  locationMetricsNode: any;
  stationMetricsNode: any;
  layoutDetails: any;
  isLoading: any;
  clientId: any;
}
const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    autoComplete: {
      marginTop: "4px",
      marginLeft: "12px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_background,
        borderRadius: 10,
      },
      "& fieldset": {
        borderRadius: 10,
        border: "none",
        color: theme.palette.custom.orange.contrastText,
      },
    },
  }),
);
const WidgetDetailsModal: React.FunctionComponent<DashboardCreateModalProps> = ({
  locationMetricsNode,
  stationMetricsNode,
  layoutDetails,
  isLoading,
  clientId,
}) => {
  const [layout, setLayout] = useState<any>([]);
  const [configDetails, setConfigDetails] = useState<any>({});
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second
    return () => clearInterval(interval); // Cleanup on unmount
  }, []);
  const [fontScale, setFontScale] = useState(1);
  const [userFontScale, setUserFontScale] = useState(1);
  useEffect(() => {
    const updateFontScale = () => {
      const scale = Math.min(1, window.innerWidth / GRID_BASE_WIDTH);
      setFontScale(Math.max(MIN_FONT_SCALE, scale));
    };
    updateFontScale();
    window.addEventListener("resize", updateFontScale);
    return () => window.removeEventListener("resize", updateFontScale);
  }, []);
  const handleIncreaseFontSize = () => {
    setUserFontScale((prev: number) =>
      Math.min(MAX_USER_FONT_SCALE, prev + USER_FONT_SCALE_STEP),
    );
  };
  const handleDecreaseFontSize = () => {
    setUserFontScale((prev: number) =>
      Math.max(MIN_USER_FONT_SCALE, prev - USER_FONT_SCALE_STEP),
    );
  };
  const gridContainerRef = useRef<any>(null);
  const [rowHeight, setRowHeight] = useState(120);
  useEffect(() => {
    const updateRowHeight = () => {
      const totalRows = (layout?.data || []).reduce(
        (max: number, item: any) =>
          Math.max(max, (item?.y || 0) + (item?.h || 1)),
        0,
      );
      if (!totalRows) {
        return;
      }
      const gridTop = gridContainerRef.current
        ? gridContainerRef.current.getBoundingClientRect().top
        : 0;
      const availableHeight =
        window.innerHeight - gridTop - GRID_BOTTOM_SPACING;
      const nextRowHeight =
        (availableHeight - (totalRows + 1) * GRID_MARGIN_Y) / totalRows;
      setRowHeight(Math.max(MIN_ROW_HEIGHT, nextRowHeight));
    };
    updateRowHeight();
    window.addEventListener("resize", updateRowHeight);
    return () => window.removeEventListener("resize", updateRowHeight);
  }, [layout]);
  const getValueByPath = (obj: any, path: string[]) => {
    return path.reduce((acc, key) => acc?.[key], obj);
  };
  const { globalState, setGlobalState } = useContext(GlobalContext);
  useEffect(() => {
    setLayout(layoutDetails);
    setConfigDetails(layoutDetails?.config);
    if (layoutDetails) {
      if (layoutDetails?.config?.isDarkMode === null) {
        setGlobalState({ isDarkMode: false });
        setLocalStore("isDarkMode", false);
      } else if (layoutDetails?.config?.isDarkMode) {
        setGlobalState({ isDarkMode: true });
        setLocalStore("isDarkMode", true);
      } else {
        setGlobalState({ isDarkMode: false });
        setLocalStore("isDarkMode", false);
      }
    }
  }, [layoutDetails]);
  const formattedTime = currentTime.toLocaleString(); // e.g., "6/6/2025, 10:23:45 AM"
  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Typography variant="body2" align="left" style={{ marginLeft: "12px" }}>
          {formattedTime}
        </Typography>
        <IconButton
          size="small"
          onClick={handleDecreaseFontSize}
          disabled={userFontScale <= MIN_USER_FONT_SCALE}
          style={{ marginLeft: "12px" }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleIncreaseFontSize}
          disabled={userFontScale >= MAX_USER_FONT_SCALE}
          style={{ marginLeft: "4px" }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </div>
      <div ref={gridContainerRef} style={{ marginTop: "20px" }}>
        <ReactGridLayout
          isDraggable={false}
          isResizable={false}
          rowHeight={rowHeight}
          cols={12}
          layout={layout?.data || []}
          style={{ zIndex: 0 }}
          autoSize
          draggableCancel=".no-drag"
          // useCSSTransforms={true}
        >
          {layout?.data &&
            layout?.data.map((data: any) => {
              if (
                data?.data?.main !== "headBox" &&
                data?.data?.main !== "headNormal" &&
                data?.data?.main !== "spacer"
              ) {
                const value = getValueByPath(
                  data?.data?.path[0] === "stationAverageTime"
                    ? { stationAverageTime: stationMetricsNode }
                    : locationMetricsNode,
                  data?.data?.path,
                );
                return (
                  <div key={data?.data.key}>
                    <SingleWidgetBox
                      widgetData={data?.data}
                      configDetails={configDetails}
                      clientId={clientId}
                      fontScale={fontScale * userFontScale}
                    />
                  </div>
                );
              } else if (data?.data?.main === "spacer") {
                return (
                  <div key={data?.data.key}>
                    <EmptyWidgetBoxes />
                  </div>
                );
              } else {
                return (
                  <div key={data?.data.key}>
                    <TextWidgetBox
                      widgetData={data}
                      fontScale={fontScale * userFontScale}
                    />
                  </div>
                );
              }
            })}
        </ReactGridLayout>
      </div>
    </>
  );
};
export default WithLoading(WidgetDetailsModal);
